import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { detectLocale, detectUrlLocale, persistLocale, applyDocumentDirection, clearLocale } from '../utils/localeDetection';
import { DEFAULT_LOCALE } from '../constants';
import { isLanguageSelectorEnabled } from '../components/Utils';

interface LocaleContextValue {
    locale: string;
    setLocale: (locale: string) => void;
    resetLocale: () => void;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

interface LocaleProviderProps {
    children: React.ReactNode;
}

/**
 * Get initial locale based on URL parameter and whether language selector is enabled.
 *
 * Priority:
 * 1. URL ?lang= parameter - Always honored (allows Taka kiosk admins to set default)
 * 2. For enabled sites: Use full locale detection (localStorage, browser, fallback)
 * 3. For non-enabled sites: Force English (DEFAULT_LOCALE) for consistent experience
 *
 * The ?lang= parameter takes highest priority because Taka kiosk images don't
 * propagate OS locale to Firefox's Accept-Language header. This allows per-site
 * language configuration without requiring browser/OS changes.
 */
function getInitialLocale(): string {
    // Always honor explicit URL language parameter (for Taka kiosk configuration)
    const urlLocale = detectUrlLocale();
    if (urlLocale) {
        return urlLocale;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const siteCode = urlParams.get('site_code');

    if (isLanguageSelectorEnabled(siteCode)) {
        return detectLocale();
    }

    return DEFAULT_LOCALE;
}

export const LocaleProvider: React.FC<LocaleProviderProps> = ({ children }) => {
    const [locale, setLocaleState] = useState<string>(() => getInitialLocale());

    const setLocale = useCallback((newLocale: string) => {
        persistLocale(newLocale);
        applyDocumentDirection(newLocale);
        setLocaleState(newLocale);
    }, []);

    const resetLocale = useCallback(() => {
        clearLocale();
        const newLocale = getInitialLocale();
        applyDocumentDirection(newLocale);
        setLocaleState(newLocale);
    }, []);

    // Apply document direction on initial mount based on detected locale
    useEffect(() => {
        applyDocumentDirection(locale);
    }, [locale]);

    const value = useMemo(
        () => ({ locale, setLocale, resetLocale }),
        [locale, setLocale, resetLocale]
    );

    return (
        <LocaleContext.Provider value={value}>
            {children}
        </LocaleContext.Provider>
    );
};

export const useLocale = (): LocaleContextValue => {
    const context = useContext(LocaleContext);
    if (!context) {
        throw new Error('useLocale must be used within a LocaleProvider');
    }
    return context;
};
